// The catalogue (PLAN-offers.md): every offer at its current version, public or private, with its subscribers. An
// offer is in one currency, its amounts too; the same key in another currency is another offer (PLAN-multi-currency.md).
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api';
import { useAsync } from '../../hooks/useAsync';
import { keyIn, money } from '../../services/format';
import { Alert, Badge, Button, Cell, PageTitle, Row, Table } from '../../components/ui';
import type { OfferAdmin, OfferVersion } from '../../types/api';

/** "€30 · €500 → €600", or "—" without tiers; in the version's currency. */
export function tierSummary(v: Pick<OfferVersion, 'credit_tiers' | 'currency'>, locale: string): string {
  if (!v.credit_tiers?.length) return '—';
  const m = (x: string) => money(x, v.currency, locale);
  return v.credit_tiers.map((x) => (Number(x.usage) === Number(x.price) ? m(x.price) : `${m(x.price)} → ${m(x.usage)}`)).join(' · ');
}

/** "12 / 50" when the offer limits its current subscriptions. */
export function places(o: Pick<OfferAdmin, 'subscribers' | 'max_active_subscriptions'>): string {
  return o.max_active_subscriptions === null ? String(o.subscribers) : `${o.subscribers} / ${o.max_active_subscriptions}`;
}

export function seatRange(v: Pick<OfferVersion, 'min_seats' | 'max_seats'>): string {
  return v.max_seats === null ? `${v.min_seats}+` : v.min_seats === v.max_seats ? String(v.min_seats) : `${v.min_seats}–${v.max_seats}`;
}

export function OffersList() {
  const { t, i18n } = useTranslation();
  const l = i18n.language;
  const navigate = useNavigate();
  const { data, error, loading } = useAsync(() => api.offers(), []);
  return (
    <div>
      <PageTitle title={t('offers.title')} subtitle={t('offers.subtitle')} actions={<Button variant="primary" onClick={() => navigate('/offers/new')}>{t('offers.new')}</Button>} />
      {error && <Alert>{error}</Alert>}
      <Table head={[t('offers.name'), t('offers.version'), t('offers.pricePerSeat'), t('offers.allowancePerSeat'), t('offers.tiers'), t('offers.userCap'), t('offers.seats'), t('offers.visibility'), t('offers.subscribers')]} empty={loading ? t('common.loading') : t('common.empty')}>
        {(data ?? []).map((o) => (
          <Row key={o.id} onClick={() => navigate(`/offers/${o.id}`)}>
            <Cell>
              {o.name} <span className="font-mono text-[10.5px] text-faint">{keyIn(o.key, o.terms.currency)}</span>
            </Cell>
            <Cell mono>
              v{o.terms.version}
              {o.versions.length > 1 && <span className="text-faint"> / {o.versions.length}</span>}
            </Cell>
            <Cell mono>{o.trial ? t('offers.trialFor', { days: o.terms.trial_days }) : money(o.terms.price_per_seat, o.terms.currency, l)}</Cell>
            <Cell mono>{o.terms.allowance_per_seat_eur === null ? t('orgs.unlimited') : money(o.terms.allowance_per_seat_eur, o.terms.currency, l)}</Cell>
            <Cell mono className="max-w-[260px] truncate" title={tierSummary(o.terms, l)}>
              {tierSummary(o.terms, l)}
            </Cell>
            <Cell mono>{o.terms.user_cap_eur === null ? '—' : money(o.terms.user_cap_eur, o.terms.currency, l)}</Cell>
            <Cell mono>{seatRange(o.terms)}</Cell>
            <Cell>
              {!o.active ? <Badge tone="bad">{t('offers.inactive')}</Badge> : o.public ? <Badge tone="good">{t('offers.public')}</Badge> : <Badge>{t('offers.private')}</Badge>}
            </Cell>
            <Cell mono>{places(o)}</Cell>
          </Row>
        ))}
      </Table>
    </div>
  );
}
